import { useTranslation } from 'react-i18next'
import { useForm, Controller, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import {
  Flex,
  ActionModal,
  Modal,
  Typography,
  Radio,
  Select,
  FormField,
  AcknowledgeModal,
  TextInput,
  Checkbox,
  Alert
} from '@percent/lemonade'
import {
  RejectionReason,
  RejectionReasonOption,
  SubmitCheckFormValues,
  SubmitCheckModalProps,
  SubmitCheckOutcome,
  SubmitCheckResult
} from './SubmitCheckModal.types'
import {
  CheckType,
  ProfileDataCheckReasons,
  MonetaryVerificationCheckStatus
} from '@percent/admin-dashboard/api/actions/monetaryVerifications/monetaryVerifications.types'

const CUSTOM_REJECTION_REASON_MAX_LENGTH = 255

export function SubmitCheckModal({
  isOpen,
  onClose,
  partnerName,
  organisationName,
  onSubmit,
  isPending,
  submitCheckStatus,
  checkType,
  checkStatus,
  checkTitle
}: Readonly<SubmitCheckModalProps>) {
  const { t } = useTranslation()

  const validationSchema = yup.object().shape({
    outcome: yup.string().required(t('errorMessage.required')),
    rejectionReason: yup.string().when('outcome', {
      is: 'reject',
      then: schema => schema.required(t('errorMessage.required')),
      otherwise: schema => schema.notRequired()
    }),
    customRejectionReason: yup
      .string()
      .max(CUSTOM_REJECTION_REASON_MAX_LENGTH, t('errorMessage.shouldNotExceed255'))
      .when(['rejectionReason'], {
        is: 'customReason',
        then: schema => schema.required(t('errorMessage.required')),
        otherwise: schema => schema.notRequired()
      }),
    isMistakeCorrection: yup.boolean().notRequired()
  })

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset
  } = useForm<SubmitCheckFormValues>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      isMistakeCorrection: false
    }
  })

  const [outcome, rejectionReason, customRejectionReason] = useWatch({
    control,
    name: ['outcome', 'rejectionReason', 'customRejectionReason']
  })

  const profileDataCheckRejectionReasons: RejectionReasonOption[] = [
    {
      label: t('monetaryDonationVerificationRejectionReasons.claimRejected'),
      value: ProfileDataCheckReasons.CLAIM_REJECTED
    },
    {
      label: t('monetaryDonationVerificationRejectionReasons.consentFormRejected'),
      value: ProfileDataCheckReasons.CONSENT_FORM_REJECTED
    },
    {
      label: t('monetaryDonationVerificationRejectionReasons.logoNotProvided'),
      value: ProfileDataCheckReasons.LOGO_NOT_PROVIDED
    }
  ]

  const rejectionReasons: RejectionReasonOption[] = [
    ...(checkType === CheckType.PROFILE_DATA_CHECK ? profileDataCheckRejectionReasons : []),
    {
      label: t('monetaryDonationVerificationRejectionReasons.customReason'),
      value: RejectionReason.CUSTOM_REASON
    }
  ]

  const isSubmitEnabled =
    outcome === SubmitCheckOutcome.APPROVE ||
    (outcome === SubmitCheckOutcome.REJECT &&
      !!rejectionReason &&
      (rejectionReason !== 'customReason' || !!customRejectionReason))

  const onSubmitForm = (data: SubmitCheckFormValues) => {
    onSubmit(data)
    reset()
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const successModal = submitCheckStatus === SubmitCheckResult.SUCCESS && (
    <AcknowledgeModal
      result="positive"
      title={t('dialog.submitCheck.success.title')}
      buttonText={t('button.close')}
      handleClose={handleClose}
      viewTestId="submit-check-success-modal"
      buttonTestId="close-submit-check-success-modal"
    />
  )

  const errorModal = submitCheckStatus === SubmitCheckResult.ERROR && (
    <AcknowledgeModal
      result="negative"
      title={t('dialog.submitCheck.error.title')}
      buttonText={t('button.done')}
      handleClose={handleClose}
      viewTestId="submit-check-error-modal"
      buttonTestId="close-submit-check-error-modal"
    />
  )

  return (
    <Modal open={isOpen} onClose={handleClose}>
      {successModal || errorModal || (
        <ActionModal
          title={t('dialog.submitCheck.title')}
          primaryButtonText={t('dialog.submitCheck.submitButton')}
          primaryBtnTestId="submit-check-modal-submit"
          secondaryButtonText={t('button.cancel')}
          secondaryBtnTestId="submit-check-modal-cancel"
          handleClose={handleClose}
          handleSubmit={handleSubmit(onSubmitForm)}
          disabled={!isSubmitEnabled}
          loading={isPending}
        >
          <Flex direction="column" gap={16}>
            <Typography>{t('dialog.submitCheck.description', { partnerName, organisationName })}</Typography>

            <Controller
              name="outcome"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormField
                  label={t('dialog.submitCheck.checkOutcome', { checkTitle })}
                  status={errors.outcome ? 'danger' : 'default'}
                  statusMessage={errors.outcome ? t('errorMessage.required') : undefined}
                  necessity="required"
                >
                  <Flex direction="column" gap={8}>
                    <Radio
                      id="outcome-approve"
                      name="outcome"
                      value="approve"
                      label={t('button.approve')}
                      checked={value === 'approve'}
                      onChange={e => {
                        onChange(e.target.value)

                        setValue('rejectionReason', '', { shouldValidate: true })
                        setValue('customRejectionReason', '', { shouldValidate: true })
                      }}
                    />
                    <Radio
                      id="outcome-reject"
                      name="outcome"
                      value="reject"
                      label={t('button.reject')}
                      checked={value === 'reject'}
                      onChange={e => {
                        onChange(e.target.value)

                        setValue('rejectionReason', RejectionReason.CUSTOM_REASON, { shouldValidate: true })
                      }}
                    />
                  </Flex>
                </FormField>
              )}
            />

            {outcome === 'reject' && rejectionReasons.length > 1 && (
              <Controller
                name="rejectionReason"
                control={control}
                render={({ field: { value } }) => (
                  <FormField
                    label={t('dialog.submitCheck.rejectionReason')}
                    status={errors.rejectionReason ? 'danger' : 'default'}
                    statusMessage={errors.rejectionReason ? t('errorMessage.required') : undefined}
                    necessity="required"
                  >
                    <Select
                      options={rejectionReasons}
                      defaultValue={rejectionReasons.find(option => option.value === value)}
                      placeholder={t('form.selectRejectionReason')}
                      onChange={event => {
                        setValue('rejectionReason', event.value, { shouldValidate: true })

                        if (event.value !== 'customReason') {
                          setValue('customRejectionReason', '', { shouldValidate: true })
                        }
                      }}
                    />
                  </FormField>
                )}
              />
            )}

            {outcome === 'reject' && !!rejectionReason && (
              <Alert variant="warning">
                {t('dialog.submitCheck.customRejectionReasonVisibility' as any, { partnerName })}
              </Alert>
            )}

            {outcome === 'reject' && rejectionReason === 'customReason' && (
              <Controller
                name="customRejectionReason"
                control={control}
                render={({ field: { value } }) => (
                  <FormField
                    label={t('dialog.submitCheck.customRejectionReason' as any)}
                    status={errors.customRejectionReason ? 'danger' : 'default'}
                    statusMessage={errors.customRejectionReason?.message}
                    necessity="required"
                  >
                    <TextInput
                      name="customRejectionReason"
                      value={value || ''}
                      maxLength={CUSTOM_REJECTION_REASON_MAX_LENGTH}
                      onChange={e => {
                        setValue('customRejectionReason', e.target.value, { shouldValidate: true })
                      }}
                      placeholder={t('dialog.submitCheck.customRejectionReasonPlaceholder' as any)}
                    />
                  </FormField>
                )}
              />
            )}

            {!!checkStatus && checkStatus !== MonetaryVerificationCheckStatus.PENDING && (
              <Controller
                name="isMistakeCorrection"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Checkbox
                    variant="default"
                    id="is-mistake-correction"
                    name="isMistakeCorrection"
                    label={t('dialog.submitCheck.isMistakeCorrection', { partnerName })}
                    active={!!value}
                    onChange={() => {
                      onChange(!value)
                    }}
                  />
                )}
              />
            )}
          </Flex>
        </ActionModal>
      )}
    </Modal>
  )
}
